import type { TimelineResponse } from '@fewerbags/schema';
import { formatClock, formatDuration } from '../../lib/planDates';
import { planColor } from '../../lib/planColors';
import { Alert } from '../Alert';
import { PlanColorDot } from './PlanColorDot';
import { TimelineStepCard } from './TimelineStepCard';

/** Colour key: one chip per recipe on the plan. */
export function TimelineLegend({ legend }: { legend: TimelineResponse['legend'] }) {
  if (legend.length === 0) return null;
  return (
    <ul aria-label="Recipe colours" className="flex flex-wrap gap-2">
      {legend.map((item) => {
        const color = planColor(item.colorIndex);
        return (
          <li
            key={item.recipeId}
            className="flex items-center gap-2 rounded-full border px-3 py-1 text-sm"
            style={{ backgroundColor: color.tint, borderColor: color.border, color: color.ink }}
          >
            <PlanColorDot colorIndex={item.colorIndex} />
            {item.title}
          </li>
        );
      })}
    </ul>
  );
}

/** Conflicts the planner could not resolve, with the fix it suggests (PLAN §4.1). */
export function FeasibilityBanner({ timeline }: { timeline: TimelineResponse }) {
  if (timeline.feasible && timeline.conflicts.length === 0) {
    return (
      <Alert tone="success">
        This all fits: start at {formatClock(timeline.startAt)} and everything lands together at{' '}
        {formatClock(timeline.serveAt)}.
      </Alert>
    );
  }
  return (
    <Alert tone={timeline.feasible ? 'warning' : 'error'}>
      <p className="font-semibold">
        {timeline.feasible ? 'This works, but it is tight.' : 'This does not fit as planned.'}
      </p>
      <ul className="mt-1 flex list-disc flex-col gap-1 pl-5">
        {timeline.conflicts.map((conflict, i) => (
          <li key={`${conflict.kind}-${i}`}>
            {conflict.message}
            {conflict.suggestion ? (
              <span className="block opacity-90">{conflict.suggestion}</span>
            ) : null}
          </li>
        ))}
      </ul>
    </Alert>
  );
}

/** Legend + headline times + feasibility + the merged, time-ordered step list. */
export function TimelineView({ timeline }: { timeline: TimelineResponse }) {
  const titleFor = (recipeId: string) =>
    timeline.legend.find((l) => l.recipeId === recipeId)?.title ?? 'This meal';

  return (
    <div className="flex flex-col gap-3">
      <TimelineLegend legend={timeline.legend} />

      <dl className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        <div className="rounded-lg border border-line bg-cream p-3">
          <dt className="text-xs text-muted">Start cooking at</dt>
          <dd className="text-lg font-semibold text-brand tabular-nums">
            {formatClock(timeline.startAt)}
          </dd>
        </div>
        <div className="rounded-lg border border-line bg-cream p-3">
          <dt className="text-xs text-muted">Serve at</dt>
          <dd className="text-lg font-semibold text-brand tabular-nums">
            {formatClock(timeline.serveAt)}
          </dd>
        </div>
        <div className="rounded-lg border border-line bg-cream p-3">
          <dt className="text-xs text-muted">Hands-on time</dt>
          <dd className="text-lg font-semibold text-brand">
            {formatDuration(timeline.totalActiveMin) || 'none'}
          </dd>
        </div>
      </dl>

      <FeasibilityBanner timeline={timeline} />

      {timeline.entries.length === 0 ? (
        <p className="text-sm text-muted">
          No steps to schedule yet — add a recipe with steps and the timeline builds itself.
        </p>
      ) : (
        <ol className="flex flex-col gap-2">
          {timeline.entries.map((entry) => (
            <TimelineStepCard
              key={`${entry.recipeId}-${entry.stepId}-${entry.start}`}
              entry={entry}
              recipeTitle={titleFor(entry.recipeId)}
            />
          ))}
        </ol>
      )}
    </div>
  );
}
